<template>
  <v-navigation-drawer
    :value="!!record"
    fixed
    temporary
    right
    :width="isMobile ? '100%' : 520"
    class="rd"
    @input="v => !v && $emit('close')"
  >
    <div v-if="record" class="rd__frame">
      <header class="rd__head">
        <div class="rd__top">
          <button type="button" class="rd__close" @click="$emit('close')"><v-icon>mdi-close</v-icon></button>
          <span class="rd__kind">{{ type === 'lead' ? $t('crmLead') : $t('crmDeal') }} #{{ record.id }}</span>
          <v-spacer />
          <v-menu offset-y left>
            <template #activator="{ on }">
              <button type="button" class="rd__close" v-on="on"><v-icon>mdi-dots-horizontal</v-icon></button>
            </template>
            <v-list dense class="rd__menu">
              <v-list-item @click="$emit('edit', record)"><v-icon small class="me-2">mdi-pencil-outline</v-icon>{{ $t('crmEdit') }}</v-list-item>
              <template v-if="type === 'deal'">
                <v-list-item @click="act('copy')"><v-icon small class="me-2">mdi-content-copy</v-icon>{{ $t('crmCopy') }}</v-list-item>
                <v-list-item v-if="record.status === 'open'" @click="act('pause')"><v-icon small class="me-2">mdi-pause-circle-outline</v-icon>{{ $t('crmPause') }}</v-list-item>
                <v-list-item v-if="record.status === 'paused'" @click="act('resume')"><v-icon small class="me-2">mdi-play-circle-outline</v-icon>{{ $t('crmResume') }}</v-list-item>
                <v-list-item v-if="record.status === 'won' || record.status === 'lost'" @click="act('reopen')"><v-icon small class="me-2">mdi-backup-restore</v-icon>{{ $t('crmReopen') }}</v-list-item>
              </template>
              <template v-else>
                <v-list-item v-if="!['rejected','archived','unqualified'].includes(record.status)" @click="act('reject')"><v-icon small class="me-2">mdi-cancel</v-icon>{{ $t('crmReject') }}</v-list-item>
                <v-list-item v-else @click="act('restore')"><v-icon small class="me-2">mdi-backup-restore</v-icon>{{ $t('crmRestore') }}</v-list-item>
              </template>
              <v-list-item @click="act('archive')"><v-icon small class="me-2">mdi-archive-outline</v-icon>{{ $t('crmArchive') }}</v-list-item>
              <v-list-item v-if="type === 'deal'" class="error--text" @click="act('delete')"><v-icon small color="error" class="me-2">mdi-delete-outline</v-icon>{{ $t('crmDelete') }}</v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div class="rd__id">
          <crm-avatar v-if="type === 'lead'" :name="leadName" :size="44" />
          <span v-else class="rd__deal-ico" :style="{ background: stageColor }"><v-icon color="#fff">mdi-briefcase-outline</v-icon></span>
          <div class="rd__names">
            <div class="rd__name">{{ type === 'lead' ? leadName : record.title }}</div>
            <div class="rd__meta">
              <template v-if="type === 'lead'">{{ record.company_name }}</template>
              <template v-else>{{ customer }}</template>
            </div>
          </div>
          <div v-if="type === 'deal'" class="rd__amount">
            <template v-if="record.financial_hidden || record.amount === null"><v-icon small>mdi-lock-outline</v-icon></template>
            <template v-else>{{ money(record.amount) }}</template>
          </div>
        </div>

        <!-- deal stage path -->
        <div v-if="type === 'deal' && stages.length" class="rd__path">
          <button
            v-for="(s, i) in openStages"
            :key="s.id"
            type="button"
            class="rd__step"
            :class="{ 'rd__step--done': i < currentIdx, 'rd__step--on': s.id === record.stage_id }"
            :style="{ '--sc': s.color || '#2166d1' }"
            :title="s.title"
            @click="$emit('move', record, s)"
          >
            <span>{{ s.title }}</span>
          </button>
        </div>

        <div class="rd__actions">
          <a v-if="phone" :href="'tel:' + phone" class="rd__btn" @click="$emit('log', 'call', 'done')"><v-icon small>mdi-phone-outline</v-icon>{{ $t('crmCall') }}</a>
          <button type="button" class="rd__btn" @click="$emit('log', 'call', 'done')"><v-icon small>mdi-plus-circle-outline</v-icon>{{ $t('crmLogActivity') }}</button>
          <button type="button" class="rd__btn" @click="$emit('log', 'call', 'schedule')"><v-icon small>mdi-calendar-plus</v-icon>{{ $t('crmScheduleFollowUp') }}</button>
          <template v-if="type === 'lead' && record.status !== 'converted'">
            <button type="button" class="rd__btn rd__btn--primary" @click="$emit('convert', record)"><v-icon small color="#fff">mdi-swap-horizontal</v-icon>{{ $t('crmConvert') }}</button>
          </template>
          <template v-if="type === 'deal' && record.status === 'open'">
            <button v-if="wonStage" type="button" class="rd__btn rd__btn--won" @click="$emit('move', record, wonStage)"><v-icon small color="#fff">mdi-trophy-outline</v-icon>{{ $t('crmWon') }}</button>
            <button v-if="lostStage" type="button" class="rd__btn rd__btn--lost" @click="$emit('move', record, lostStage)"><v-icon small>mdi-close</v-icon>{{ $t('crmLost') }}</button>
          </template>
        </div>

        <div class="rd__tabs">
          <button v-for="t in tabs" :key="t" type="button" :class="{ on: tab === t }" @click="tab = t">{{ $t('crmTab_' + t) }}</button>
        </div>
      </header>

      <div class="rd__body">
        <div v-if="loading" class="rd__loading"><v-progress-linear indeterminate color="primary" /></div>

        <template v-if="tab === 'info'">
          <div class="rd__kpis" v-if="type === 'deal' && detail">
            <div><b>{{ num(detail.days_in_stage) }}</b><span>{{ $t('crmDaysInStage') }}</span></div>
            <div><b>{{ num(detail.days_since_activity) }}</b><span>{{ $t('crmDaysSinceActivity') }}</span></div>
            <div :class="{ warn: detail.is_stale }"><b>{{ detail.is_stale ? $t('crmYes') : $t('crmNo') }}</b><span>{{ $t('crmStale') }}</span></div>
          </div>
          <div class="rd__kpis" v-if="type === 'lead'">
            <div><b>{{ num(record.score) }}</b><span>{{ $t('crmScore') }}</span></div>
            <div><b>{{ num(record.readiness) }}٪</b><span>{{ $t('crmReadiness') }}</span></div>
            <div><b>{{ record.first_contact_at ? fmtDate(record.first_contact_at) : '—' }}</b><span>{{ $t('crmFirstContact') }}</span></div>
          </div>
          <dl class="rd__props">
            <template v-for="p in props">
              <dt :key="p.k + 'k'">{{ p.label }}</dt>
              <dd :key="p.k + 'v'" :class="{ ltr: p.ltr }">
                <component :is="p.comp" v-if="p.comp" v-bind="p.bind" />
                <template v-else>{{ p.value || '—' }}</template>
              </dd>
            </template>
          </dl>
          <div v-if="tags.length" class="rd__tags">
            <span v-for="t in tags" :key="t" class="rd__tag">#{{ t }}</span>
          </div>
          <div v-if="record.notes || record.need" class="rd__note">
            <div class="rd__note-label">{{ type === 'lead' ? $t('crmNeed') : $t('crmNotes') }}</div>
            {{ record.need || record.notes }}
          </div>
          <div v-if="type === 'deal' && products.length" class="rd__products">
            <div class="rd__note-label">{{ $t('crmProducts') }}</div>
            <div v-for="p in products" :key="p.id" class="rd__prod">
              <span>{{ p.name || (p.product && p.product.name) }}</span>
              <span class="ltr">× {{ num(p.quantity) }}</span>
              <b v-if="p.total !== null && p.total !== undefined">{{ money(p.total) }}</b>
            </div>
          </div>
        </template>

        <template v-else-if="tab === 'activity'">
          <div v-if="!timeline.length && !loading" class="rd__empty">
            <v-icon large>mdi-timeline-clock-outline</v-icon>
            <div>{{ $t('crmNoActivity') }}</div>
            <v-btn small depressed color="primary" class="mt-2" @click="$emit('log', 'call')">{{ $t('crmLogActivity') }}</v-btn>
          </div>
          <ol class="rd__tl">
            <li v-for="a in timeline" :key="a.id" :class="'st-' + (a.status || 'done')">
              <span class="rd__tl-ico"><v-icon x-small>{{ icon(a.type) }}</v-icon></span>
              <div class="rd__tl-body">
                <div class="rd__tl-head">
                  <b>{{ a.title || $t('crmAct_' + a.type) }}</b>
                  <span class="rd__tl-time">{{ fmtDate(a.completed_at || a.due_at || a.created_at, true) }}</span>
                </div>
                <div v-if="a.result || a.content" class="rd__tl-text">{{ a.result || a.content }}</div>
                <div class="rd__tl-foot">
                  <crm-avatar :user-id="a.owner_user_id" :size="18" />
                  <span>{{ memberName(a.owner_user_id) }}</span>
                  <span v-if="a.status === 'open'" class="rd__tl-open">{{ $t('crmScheduled') }}</span>
                </div>
              </div>
            </li>
          </ol>
        </template>

        <template v-else>
          <div v-if="!history.length && !loading" class="rd__empty"><v-icon large>mdi-history</v-icon><div>{{ $t('crmNoHistory') }}</div></div>
          <ol class="rd__hist">
            <li v-for="(h, i) in history" :key="i">
              <div class="rd__hist-head">
                <crm-avatar :user-id="h.user_id" :size="20" />
                <b>{{ memberName(h.user_id) || '—' }}</b>
                <span>{{ $t('crmAction_' + h.action) }}</span>
                <span class="rd__tl-time">{{ fmtDate(h.created_at, true) }}</span>
              </div>
              <div v-for="k in changedKeys(h)" :key="k" class="rd__diff">
                <span class="rd__diff-k">{{ k }}</span>
                <s>{{ show(h.before && h.before[k]) }}</s>
                <v-icon x-small>mdi-arrow-left</v-icon>
                <b>{{ show(h.after && h.after[k]) }}</b>
              </div>
            </li>
          </ol>
        </template>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import api from '../crmApi'
import { crm, money, fmtDate, memberName, fullName, dealCustomer, sourceTitle, ACTIVITY_ICONS, pick } from '../crmStore'
import { CrmChip, CrmAvatar } from './CrmBits'

export default {
  name: 'CrmRecordDrawer',
  components: { CrmChip, CrmAvatar },
  props: { type: { type: String, default: 'deal' }, record: { type: Object, default: null }, reloadKey: { type: Number, default: 0 } },
  data: () => ({ tab: 'info', detail: null, timeline: [], history: [], loading: false }),
  computed: {
    tabs () { return ['info', 'activity', 'history'] },
    leadName () { return this.record ? (fullName(this.record) || this.record.company_name) : '' },
    customer () { return dealCustomer(this.record) },
    phone () {
      const r = this.record
      if (!r) return ''
      return r.mobile || r.phone || (r.person && (r.person.mobile || r.person.phone)) || ''
    },
    pipeline () { return this.record ? crm.pipelines.find(p => p.id === this.record.pipeline_id) : null },
    stages () { return this.pipeline ? this.pipeline.stages || [] : [] },
    openStages () { return this.stages.filter(s => s.kind === 'open') },
    currentIdx () { return this.openStages.findIndex(s => s.id === this.record.stage_id) },
    wonStage () { return this.stages.find(s => s.kind === 'won') },
    lostStage () { return this.stages.find(s => s.kind === 'lost') },
    stageColor () {
      const s = this.stages.find(x => x.id === this.record.stage_id)
      return (s && s.color) || '#2166d1'
    },
    tags () { return Array.isArray(this.record.tags) ? this.record.tags : [] },
    products () { return (this.detail && this.detail.deal && this.detail.deal.products) || this.record.products || [] },
    props () {
      const r = this.record
      if (this.type === 'lead') {
        return [
          { k: 'status', label: this.$t('crmStatus'), comp: 'crm-chip', bind: { value: r.status } },
          { k: 'priority', label: this.$t('crmPriority'), comp: 'crm-chip', bind: { kind: 'priority', value: r.priority } },
          { k: 'owner', label: this.$t('crmOwner'), value: memberName(r.owner_user_id) || this.$t('crmUnassigned') },
          { k: 'mobile', label: this.$t('crmMobile'), value: r.mobile, ltr: true },
          { k: 'email', label: this.$t('crmEmail'), value: r.email, ltr: true },
          { k: 'source', label: this.$t('crmSource'), value: (r.source && r.source.title) || sourceTitle(r.source_id) },
          { k: 'next', label: this.$t('crmNextFollowUp'), value: fmtDate(r.next_follow_up_at, true) },
          { k: 'last', label: this.$t('crmLastActivity'), value: fmtDate(r.last_activity_at, true) },
          { k: 'created', label: this.$t('crmCreatedAt'), value: fmtDate(r.created_at) }
        ]
      }
      const st = this.stages.find(s => s.id === r.stage_id)
      return [
        { k: 'status', label: this.$t('crmStatus'), comp: 'crm-chip', bind: { value: r.status } },
        { k: 'stage', label: this.$t('crmStage'), value: (r.stage && r.stage.title) || (st && st.title) },
        { k: 'priority', label: this.$t('crmPriority'), comp: 'crm-chip', bind: { kind: 'priority', value: r.priority } },
        { k: 'owner', label: this.$t('crmOwner'), value: memberName(r.owner_user_id) || this.$t('crmUnassigned') },
        { k: 'prob', label: this.$t('crmProbability'), value: (r.probability || 0) + '٪' },
        { k: 'close', label: this.$t('crmExpectedClose'), value: fmtDate(r.expected_close_date) },
        { k: 'next', label: this.$t('crmNextFollowUp'), value: fmtDate(r.next_follow_up_at, true) },
        { k: 'last', label: this.$t('crmLastActivity'), value: fmtDate(r.last_activity_at, true) },
        { k: 'loss', label: this.$t('crmLossReason'), value: r.loss_reason && r.loss_reason.title },
        { k: 'created', label: this.$t('crmCreatedAt'), value: fmtDate(r.created_at) }
      ].filter(p => p.k !== 'loss' || r.status === 'lost')
    }
  },
  watch: {
    record (v, old) {
      if (v && (!old || v.id !== old.id)) { this.tab = 'info'; this.load() }
    },
    reloadKey () { if (this.record) this.load() },
    tab () { if (this.record) this.loadTab() }
  },
  methods: {
    money,
    fmtDate,
    memberName,
    num (v) { return v === null || v === undefined ? '—' : Number(v).toLocaleString(this.$i18n.locale === 'en' ? 'en-US' : 'fa-IR') },
    icon (t) { return ACTIVITY_ICONS[t] || 'mdi-circle-small' },
    show (v) {
      if (v === null || v === undefined || v === '') return '—'
      if (Array.isArray(v)) return v.join('، ')
      if (typeof v === 'object') return JSON.stringify(v)
      return String(v)
    },
    changedKeys (h) {
      return Object.keys({ ...(h.before || {}), ...(h.after || {}) }).filter(k => !['updated_at', 'created_at'].includes(k)).slice(0, 8)
    },
    async load () {
      this.detail = null
      this.timeline = []
      this.history = []
      this.loading = true
      try {
        const res = this.type === 'lead' ? await api.lead(this.record.id) : await api.deal(this.record.id)
        this.detail = res
        this.history = (res && res.history) || []
        const fresh = res && (res.lead || res.deal)
        if (fresh) this.$emit('refreshed', fresh)
      } catch (e) { /* drawer still shows the list row */ } finally {
        this.loading = false
      }
      this.loadTab()
    },
    async loadTab () {
      if (this.tab !== 'activity') return
      this.loading = true
      try {
        const res = await api.timeline({ entity_type: this.type, entity_id: this.record.id, itemsPerPage: 50 })
        this.timeline = pick(res)
      } catch (e) { this.timeline = [] } finally { this.loading = false }
    },
    act (name) { this.$emit('action', name, this.record) }
  }
}
</script>

<style scoped>
.rd ::v-deep .v-navigation-drawer__content { overflow: hidden; }
.rd__frame { display: flex; flex-direction: column; height: 100%; }
.rd__head { flex-shrink: 0; padding: 12px 18px 0; border-bottom: 1px solid rgba(15, 23, 42, .08); }
.rd__top { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.rd__close { width: 34px; height: 34px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: inline-flex; align-items: center; justify-content: center; }
.theme--dark .rd__close { background: rgba(255, 255, 255, .08); }
.rd__kind { font-size: .75rem; opacity: .6; }
.rd__id { display: flex; align-items: center; gap: 12px; }
.rd__deal-ico { width: 44px; height: 44px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rd__names { flex: 1; min-width: 0; }
.rd__name { font-weight: 700; font-size: 1.08rem; line-height: 1.4; }
.rd__meta { font-size: .8rem; opacity: .65; }
.rd__amount { font-weight: 800; font-size: 1.05rem; white-space: nowrap; }
.rd__path { display: flex; gap: 3px; margin-top: 16px; }
.rd__step { flex: 1; min-width: 0; height: 30px; padding: 0 6px; font-size: .7rem; font-weight: 600; background: rgba(127, 127, 127, .12); clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 50%, 100% 100%, 8px 100%, 0 50%); transition: all .15s; }
.rd__step:first-child { clip-path: polygon(0 0, 100% 0, calc(100% - 8px) 50%, 100% 100%, 0 100%); border-radius: 0 6px 6px 0; }
.rd__step span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rd__step--done { background: var(--sc); color: #fff; opacity: .55; }
.rd__step--on { background: var(--sc); color: #fff; }
.rd__step:hover { filter: brightness(.95); }
.rd__actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.rd__btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); font-size: .8rem; font-weight: 600; color: inherit; text-decoration: none; }
.rd__btn--primary { background: var(--v-primary-base); border-color: var(--v-primary-base); color: #fff; }
.rd__btn--won { background: #0f9d76; border-color: #0f9d76; color: #fff; }
.rd__btn--lost { color: #c0392b; border-color: rgba(192, 57, 43, .4); }
.rd__btn--lost ::v-deep .v-icon { color: #c0392b !important; }
.rd__tabs { display: flex; gap: 18px; margin-top: 14px; }
.rd__tabs button { padding: 10px 0; font-size: .85rem; font-weight: 600; opacity: .6; border-bottom: 2px solid transparent; }
.rd__tabs button.on { opacity: 1; color: var(--v-primary-base); border-bottom-color: var(--v-primary-base); }
.rd__body { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 18px; position: relative; }
.rd__loading { position: absolute; top: 0; inset-inline: 0; }
.rd__kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
.rd__kpis > div { padding: 10px 12px; border-radius: 6px; background: rgba(127, 127, 127, .07); display: flex; flex-direction: column; gap: 2px; }
.rd__kpis b { font-size: 1rem; }
.rd__kpis span { font-size: .72rem; opacity: .6; }
.rd__kpis .warn { background: rgba(217, 130, 43, .12); color: #b86a1a; }
.rd__props { display: grid; grid-template-columns: 130px 1fr; gap: 12px 10px; font-size: .85rem; margin: 0; }
.rd__props dt { opacity: .6; }
.rd__props dd { margin: 0; font-weight: 500; }
.rd__props .ltr { direction: ltr; text-align: end; }
.rd__tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 16px; }
.rd__tag { font-size: .75rem; padding: 2px 8px; border-radius: 6px; background: rgba(25, 118, 210, .08); color: var(--v-primary-base); }
.rd__note, .rd__products { margin-top: 18px; padding: 12px 14px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .08); font-size: .85rem; white-space: pre-line; }
.rd__note-label { font-size: .72rem; opacity: .55; margin-bottom: 4px; }
.rd__prod { display: flex; gap: 10px; padding: 4px 0; }
.rd__prod span:first-child { flex: 1; }
.rd__empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 40px 0; opacity: .7; font-size: .85rem; }
.rd__tl { list-style: none; padding: 0; margin: 0; position: relative; }
.rd__tl::before { content: ''; position: absolute; inset-inline-start: 13px; top: 6px; bottom: 6px; width: 2px; background: rgba(127, 127, 127, .15); }
.rd__tl li { display: flex; gap: 12px; padding-bottom: 16px; position: relative; }
.rd__tl-ico { width: 28px; height: 28px; border-radius: 50%; background: #fff; border: 2px solid rgba(25, 118, 210, .35); display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 1; }
.theme--dark .rd__tl-ico { background: #1e1e1e; }
.rd__tl li.st-open .rd__tl-ico { border-style: dashed; border-color: #d9822b; }
.rd__tl-body { flex: 1; min-width: 0; padding: 8px 12px; border-radius: 6px; background: rgba(127, 127, 127, .06); }
.rd__tl-head { display: flex; gap: 8px; align-items: baseline; font-size: .84rem; }
.rd__tl-head b { flex: 1; }
.rd__tl-time { font-size: .72rem; opacity: .55; white-space: nowrap; }
.rd__tl-text { font-size: .82rem; margin-top: 4px; white-space: pre-line; }
.rd__tl-foot { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: .72rem; opacity: .75; }
.rd__tl-open { margin-inline-start: auto; color: #d9822b; font-weight: 600; }
.rd__hist { list-style: none; padding: 0; margin: 0; }
.rd__hist li { padding: 10px 0; border-bottom: 1px solid rgba(15, 23, 42, .06); }
.rd__hist-head { display: flex; align-items: center; gap: 6px; font-size: .82rem; }
.rd__hist-head .rd__tl-time { margin-inline-start: auto; }
.rd__diff { display: flex; align-items: center; gap: 6px; font-size: .78rem; margin-top: 4px; padding-inline-start: 26px; }
.rd__diff-k { opacity: .55; min-width: 80px; }
.rd__diff s { opacity: .55; }
.rd__menu .v-list-item { min-height: 36px; font-size: .85rem; }
</style>
